/**
 * Notification Bell
 *
 * Header icon with unread count badge.
 * Toggles the notification panel on click.
 */

import { Bell } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useNotificationStore } from "@/stores/useNotificationStore";
import { useUnreadCount } from "@/hooks/useNotifications";
import { cn } from "@/lib/utils";

interface NotificationBellProps {
  className?: string;
}

export function NotificationBell({ className }: NotificationBellProps) {
  // Server unread count already includes durable chat-message notifications.
  const { unreadCount } = useUnreadCount();
  const togglePanel = useNotificationStore((s) => s.togglePanel);
  const isPanelOpen = useNotificationStore((s) => s.isPanelOpen);

  const displayCount = unreadCount > 99 ? "99+" : unreadCount;
  const hasUnread = unreadCount > 0;

  return (
    <button
      className={cn(
        "relative flex items-center justify-center",
        "h-10 w-10 border-none bg-transparent",
        "text-foreground cursor-pointer transition-all duration-200",
        "hover:bg-accent/30 active:scale-95 p-0",
        className,
      )}
      style={{ borderRadius: "8px" }}
      onClick={togglePanel}
      aria-label={`Notifications${hasUnread ? ` (${unreadCount} unread)` : ""}`}
      aria-expanded={isPanelOpen}
      aria-haspopup="true"
    >
      <Bell className="h-5 w-5 transition-transform duration-300" />

      {hasUnread && (
        <>
          <Badge
            variant="default"
            data-testid="notification-unread-count"
            className={cn(
              "absolute -top-0.5 -right-0.5 h-4 min-w-4 px-0.5",
              "flex items-center justify-center",
              "text-[9px] font-semibold",
              "animate-in zoom-in-50 duration-200",
            )}
          >
            {displayCount}
          </Badge>
          <span className="absolute -top-0.5 -right-0.5 h-4 w-4 rounded-full bg-primary/30 animate-pulse" />
        </>
      )}
    </button>
  );
}
